import { useCallback, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';

import { PolaroidCard } from './PolaroidCard';
import { PhotoDetail } from './PhotoDetail';
import { getScatterOffset, getImageUrl, seedFromHex } from '@/lib/polaroid-utils';
import { cn } from '@/lib/utils';

const PHOTOS_PER_PAGE = 4;

function chunk<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

/** Decorative corner tape strips — CSS-only scrapbook feel */
function CornerTapes({ seed }: { seed: string }) {
  const tapeColors = ['bg-pol-pink/40', 'bg-pol-mint/50', 'bg-pol-lilac/40', 'bg-pol-blue/40', 'bg-pol-pink-glitter/40'];
  const topLeft = tapeColors[seedFromHex(seed, 0, 2) % tapeColors.length];
  const topRight = tapeColors[seedFromHex(seed, 2, 4) % tapeColors.length];
  const bottomLeft = tapeColors[seedFromHex(seed, 4, 6) % tapeColors.length];
  const bottomRight = tapeColors[seedFromHex(seed, 6, 8) % tapeColors.length];

  return (
    <>
      <div className={cn('absolute -top-1 -left-1 w-10 h-3 rounded-sm rotate-[-35deg] z-10', topLeft)} />
      <div className={cn('absolute -top-1 -right-1 w-10 h-3 rounded-sm rotate-[35deg] z-10', topRight)} />
      <div className={cn('absolute -bottom-1 -left-1 w-10 h-3 rounded-sm rotate-[35deg] z-10', bottomLeft)} />
      <div className={cn('absolute -bottom-1 -right-1 w-10 h-3 rounded-sm rotate-[-35deg] z-10', bottomRight)} />
    </>
  );
}

/**
 * A single album page surface — paper texture with corner tapes, gutter shadow,
 * a fixed 4-slot photo grid, and page number.
 */
function PageSurface({ photos, pageNumber, totalPages, showAuthor, onPhotoClick, className, style }: {
  photos: NostrEvent[];
  pageNumber: number;
  totalPages: number;
  showAuthor: boolean;
  onPhotoClick: (event: NostrEvent) => void;
  className?: string;
  style?: React.CSSProperties;
}) {
  const pageSeed = photos[0]?.id ?? `page-${pageNumber}`;

  // Fixed 4-slot array
  const slots: (NostrEvent | null)[] = [];
  for (let i = 0; i < PHOTOS_PER_PAGE; i++) {
    slots.push(photos[i] ?? null);
  }

  return (
    <div
      className={cn(
        'paper-texture rounded-md border border-cream-dark/60 shadow-[0_2px_16px_rgba(45,27,78,0.06),inset_0_1px_0_rgba(255,255,255,0.5)]',
        className,
      )}
      style={style}
    >
      <CornerTapes seed={pageSeed} />

      {/* Book gutter shadow */}
      <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-black/[0.03] to-transparent rounded-l-md pointer-events-none z-[5]" />

      {/* Fixed 4-slot grid */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-5 p-5 pt-6">
        {slots.map((event, i) => {
          if (event) {
            const offset = getScatterOffset(event.id);
            return (
              <div
                key={event.id}
                style={{ transform: `translate(${offset.x}px, ${offset.y}px)` }}
              >
                <PolaroidCard
                  event={event}
                  variant={showAuthor ? 'feed-grid' : 'grid'}
                  index={i}
                  onPhotoClick={onPhotoClick}
                />
              </div>
            );
          }
          return (
            <div key={`empty-${i}`} className="invisible">
              <div className="bg-polaroid-white rounded-[3px] p-2 pb-0 aspect-[3/4]" />
            </div>
          );
        })}
      </div>

      {/* Page number */}
      {totalPages > 1 && (
        <div className="absolute bottom-2 right-4 font-hand text-[13px] text-pol-text-caption/50 z-20">
          pg. {pageNumber + 1}
        </div>
      )}
    </div>
  );
}

interface ScrapbookPagesProps {
  pictures: NostrEvent[];
  showAuthor?: boolean;
}

export function ScrapbookPages({ pictures, showAuthor = false }: ScrapbookPagesProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedPhoto, setSelectedPhoto] = useState<NostrEvent | null>(null);
  const [isFlipping, setIsFlipping] = useState(false);
  const [flipDirection, setFlipDirection] = useState<'next' | 'prev'>('next');
  /** The page being flipped TO — shown underneath during the flip */
  const [targetPage, setTargetPage] = useState<number | null>(null);
  const touchRef = useRef<{ startX: number; startY: number } | null>(null);

  const pages = chunk(pictures, PHOTOS_PER_PAGE);
  const totalPages = pages.length;

  const FLIP_DURATION = 450; // ms — slower for realism

  const goToPage = useCallback((page: number) => {
    if (page < 0 || page >= totalPages || page === currentPage || isFlipping) return;
    const dir = page > currentPage ? 'next' : 'prev';
    setFlipDirection(dir);
    setTargetPage(page);
    setIsFlipping(true);

    // When animation ends, swap to the target page
    setTimeout(() => {
      setCurrentPage(page);
      setTargetPage(null);
      setIsFlipping(false);
    }, FLIP_DURATION);
  }, [currentPage, totalPages, isFlipping, FLIP_DURATION]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY };
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchRef.current) return;
    const diffX = e.changedTouches[0].clientX - touchRef.current.startX;
    const diffY = e.changedTouches[0].clientY - touchRef.current.startY;

    if (Math.abs(diffX) > Math.abs(diffY) * 1.5) {
      const threshold = window.innerWidth * 0.2;
      if (diffX < -threshold) goToPage(currentPage + 1);
      else if (diffX > threshold) goToPage(currentPage - 1);
    }
    touchRef.current = null;
  };

  const currentPhotos = pages[currentPage] ?? [];
  const targetPhotos = targetPage !== null ? (pages[targetPage] ?? []) : [];

  // Preload images from other pages
  const preloadUrls = useMemo(() => {
    const urls: string[] = [];
    for (let p = 0; p < pages.length; p++) {
      if (p === currentPage) continue;
      for (const event of pages[p]) {
        const url = getImageUrl(event);
        if (url) urls.push(url);
      }
    }
    return urls;
  }, [pages, currentPage]);

  return (
    <>
      {/* The scrapbook "book" — stays still while pages flip */}
      <div
        className="relative"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Layer 1 (bottom): Destination page — always visible underneath during flip */}
        {isFlipping && targetPage !== null && (
          <PageSurface
            photos={targetPhotos}
            pageNumber={targetPage}
            totalPages={totalPages}
            showAuthor={showAuthor}
            onPhotoClick={setSelectedPhoto}
            className="absolute inset-0 z-0"
            style={{ position: 'absolute' }}
          />
        )}

        {/* Layer 2 (top): Current page — flips away to reveal the page underneath */}
        <PageSurface
          photos={currentPhotos}
          pageNumber={currentPage}
          totalPages={totalPages}
          showAuthor={showAuthor}
          onPhotoClick={setSelectedPhoto}
          className="relative z-10"
          style={{
            transformOrigin: flipDirection === 'next' ? 'left center' : 'right center',
            backfaceVisibility: 'hidden',
            animation: isFlipping
              ? `page-flip-${flipDirection} ${FLIP_DURATION}ms cubic-bezier(0.4, 0, 0.6, 1) forwards`
              : 'none',
          }}
        />

        {/* Swipe hint on first page */}
        {totalPages > 1 && currentPage === 0 && !isFlipping && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 font-hand text-[12px] text-pol-text-secondary/40 animate-pulse z-20">
            swipe to flip →
          </div>
        )}
      </div>

      {/* Navigation controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-4 pb-2">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 0 || isFlipping}
            className={cn(
              'w-8 h-8 rounded-full flex items-center justify-center transition-all',
              currentPage === 0
                ? 'text-pol-text-secondary/20 cursor-default'
                : 'bg-white/70 hover:bg-white text-pol-text-secondary hover:text-pol-text shadow-sm active:scale-90',
            )}
            aria-label="Previous page"
          >
            <ChevronLeft size={18} />
          </button>

          <div className="flex items-center gap-2">
            {pages.map((_, i) => (
              <button
                key={i}
                onClick={() => goToPage(i)}
                disabled={isFlipping}
                className={cn(
                  'w-2 h-2 rounded-full transition-all duration-200',
                  i === currentPage
                    ? 'bg-pol-pink scale-125'
                    : 'bg-pol-pink/30 hover:bg-pol-pink/50',
                )}
                aria-label={`Go to page ${i + 1}`}
              />
            ))}
          </div>

          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages - 1 || isFlipping}
            className={cn(
              'w-8 h-8 rounded-full flex items-center justify-center transition-all',
              currentPage === totalPages - 1
                ? 'text-pol-text-secondary/20 cursor-default'
                : 'bg-white/70 hover:bg-white text-pol-text-secondary hover:text-pol-text shadow-sm active:scale-90',
            )}
            aria-label="Next page"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}

      {/* Photo detail */}
      {selectedPhoto && (
        <PhotoDetail
          event={selectedPhoto}
          onClose={() => setSelectedPhoto(null)}
        />
      )}

      {/* Hidden preloader */}
      <div className="hidden" aria-hidden="true">
        {preloadUrls.map((url) => (
          <img key={url} src={url} alt="" loading="eager" />
        ))}
      </div>
    </>
  );
}
